import {
    PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react';
import sentryInit from '../sentry';
import { WidgetConfigResponse } from 'rwl-shared/dist/models';
import { V2NeighborhoodResponse } from '../fetchers/v2/v2NeighborhoodTypes';

const NEIGHBORHOOD_SENTRY_DSN =
    'https://3925a409cbe794d4ec1b4809a5b6a348@o1021052.ingest.us.sentry.io/4510364694675457';

export type BumpOutTest = {
    openPanel: 'left' | 'right';
    tabLocation: 'top' | 'middle' | 'bottom';
    requiresPostCode: boolean;
    ctaText: string;
    font: 'inherit' | 'Arial' | 'Helvetica' | 'Times New Roman';
    tabBackgroundColor: string;
    tabTextColor: string;
    animateTab: boolean;
};

export type GlobalConfig = {
    accountId: string;
    apiBaseUrl: string;
    brandColor?: string;
    sentryEnvironment?: string;
    config?: WidgetConfigResponse;
    bumpOutTestEnabled?: boolean;
    bumpOutTest?: BumpOutTest;
    publicUrl?: string;
    widgetHeight?: string;
    showPoweredBy?: boolean;
    /**
     * Base URL of the rw26 v2 widget service — a different host than
     * `apiBaseUrl`. Supplied by the plugin, which resolves it from a
     * per-environment table keyed on the Parse Server hostname
     * (`client/plugin/src/services/v2Host.ts`). Undefined when the environment
     * has no rw26 deployment, such as staging; `useV2Data` is what grants use
     * of it.
     */
    v2ServiceUrl?: string;
    /**
     * The `widget-service-data` flag for this account — the same switch that gates
     * the plugin's map/page/carousel and the SEO widget, so an account moves all
     * of them to v2 at once. Both this and `v2ServiceUrl` must be set before the
     * widget calls v2.
     */
    useV2Data?: boolean;
    /**
     * Page 1 of the v2 service-area list, prefetched by the plugin so the widget
     * does not have to wait for its own round trip after the bundle loads.
     * Fetched with no postcode, so it is the account's whole list.
     */
    v2ServiceAreaData?: V2NeighborhoodResponse;
};

type GlobalContextType = GlobalConfig & {
    isReady: boolean;
    setBrandColor: (brandColor: string) => void;
    setConfig: (config: any) => void;
    setIsReady: (isReady: boolean) => void;
};

const DEFAULT_BRAND_COLOR = '#CFFF04';
const DEFAULT_SENTRY_ENVIRONMENT = 'local';

/**
 * The type-trickery here is intentional.  We need the context to be undefined until the global state is loaded.
 * However we also need to be able to use the context without checking for undefined everywhere always.
 * We block rendering any children until the global state is loaded, so we can safely assume that the context
 * will be defined by the time we need to use it.
 */
const GlobalContext = createContext<GlobalContextType>(
    undefined as unknown as GlobalContextType
);

export const GlobalProvider = ({
    children,
    ...props
}: PropsWithChildren<GlobalConfig>): JSX.Element | null => {
    const [brandColor, setBrandColor] = useState<string>(
        props.brandColor ?? DEFAULT_BRAND_COLOR
    );
    const [config, setConfig] = useState<WidgetConfigResponse | undefined>(
        props.config
    );
    const [isReady, setIsReady] = useState(false);
    const [sentryEnvironment] = useState(props.sentryEnvironment); // No setter, can only init sentry once

    useEffect(() => {
        sentryInit({
            dsn: NEIGHBORHOOD_SENTRY_DSN,
            environment: sentryEnvironment?.length
                ? sentryEnvironment
                : DEFAULT_SENTRY_ENVIRONMENT,
        });
    }, []);

    useEffect(() => {
        if (config) {
            setBrandColor(
                config.account.mapSettings.color || DEFAULT_BRAND_COLOR
            );
        }
    }, [config]);

    return (
        <GlobalContext.Provider
            value={{
                // Spread rather than enumerate. Every GlobalConfig field used to be
                // copied across by hand, so any prop whose name was not repeated
                // here was accepted by the type and then silently dropped — which
                // is what happened to EN-64's `v2ServiceAreaData` (app#4955): the
                // plugin prefetched v2 data into a prop nothing could read, and
                // app#5101 removed the prefetch as dead weight. `publicUrl` was
                // being dropped the same way. `children` is already destructured
                // out, so this is exactly GlobalConfig.
                ...props,
                // State-derived values below intentionally shadow their props.
                brandColor,
                setBrandColor,
                sentryEnvironment,
                config,
                setConfig,
                isReady,
                setIsReady: (isReady: boolean) => {
                    if (isReady) {
                        console.info('RWL Widget initialized');
                    }
                    setIsReady(isReady);
                },
            }}
        >
            {children}
        </GlobalContext.Provider>
    );
};

export const useGlobal = () => {
    const context = useContext(GlobalContext);
    if (context === undefined) {
        throw new Error('useGlobal must be used within a GlobalProvider');
    }
    return context!;
};

export const WhenGlobalReady = ({ children }: PropsWithChildren<unknown>) => {
    const { isReady } = useGlobal();
    if (!isReady) {
        console.info('Waiting for app to initialize...');
        return null;
    }
    return <>{children}</>;
};
